import React, { useEffect, useState } from "react";
import { Save, Loader2 } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";

export default function SiteSettings() {
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/site-settings");
                setData(data);
            } catch (e) {
                toast.error(formatApiError(e));
            }
        })();
    }, []);

    const update = (path, value) => {
        setData((prev) => {
            const next = { ...prev };
            const segs = path.split(".");
            let obj = next;
            for (let i = 0; i < segs.length - 1; i++) {
                obj[segs[i]] = { ...(obj[segs[i]] || {}) };
                obj = obj[segs[i]];
            }
            obj[segs[segs.length - 1]] = value;
            return next;
        });
    };

    const handleSave = async () => {
        if (!data) return;
        setSaving(true);
        try {
            const payload = {
                company_name: data.company_name,
                tagline: data.tagline,
                logo_url: data.logo_url,
                favicon_url: data.favicon_url,
                phone: data.phone,
                email: data.email,
                whatsapp: data.whatsapp,
                address: data.address,
                social_media: data.social_media,
                seo_defaults: data.seo_defaults,
                analytics_id: data.analytics_id,
                sendgrid_api_key: data.sendgrid_api_key,
                resend_api_key: data.resend_api_key,
                sender_email: data.sender_email,
                google_maps_embed_url: data.google_maps_embed_url,
                maintenance_mode: data.maintenance_mode,
                maintenance_message: data.maintenance_message,
            };
            const { data: saved } = await api.put("/admin/site-settings", payload);
            setData(saved);
            toast.success("Ayarlar kaydedildi");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    if (!data) return <div className="text-sm text-slate-500">Yükleniyor...</div>;

    return (
        <div className="space-y-6" data-testid="admin-site-settings">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-dark-navy">Site Ayarları</h1>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-dark-navy hover:bg-deep-blue text-white text-sm font-semibold px-4 py-2 rounded-md inline-flex items-center gap-2"
                    data-testid="settings-save-btn"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Kaydet
                </button>
            </div>

            <Section title="Firma Bilgileri">
                <Field label="Firma Adı" value={data.company_name} onChange={(v) => update("company_name", v)} testId="settings-company-name" />
                <Field label="Tagline" value={data.tagline} onChange={(v) => update("tagline", v)} />
                <Field label="Logo URL" value={data.logo_url} onChange={(v) => update("logo_url", v)} />
                <Field label="Favicon URL" value={data.favicon_url} onChange={(v) => update("favicon_url", v)} />
            </Section>

            <Section title="İletişim">
                <Field label="Telefon" value={data.phone} onChange={(v) => update("phone", v)} testId="settings-phone" />
                <Field label="E-posta" value={data.email} onChange={(v) => update("email", v)} />
                <Field label="WhatsApp" value={data.whatsapp} onChange={(v) => update("whatsapp", v)} />
                <Field label="Adres" value={data.address} onChange={(v) => update("address", v)} />
            </Section>

            <Section title="Sosyal Medya">
                <Field label="Facebook" value={data.social_media?.facebook || ""} onChange={(v) => update("social_media.facebook", v)} />
                <Field label="Instagram" value={data.social_media?.instagram || ""} onChange={(v) => update("social_media.instagram", v)} />
                <Field label="LinkedIn" value={data.social_media?.linkedin || ""} onChange={(v) => update("social_media.linkedin", v)} />
                <Field label="Twitter" value={data.social_media?.twitter || ""} onChange={(v) => update("social_media.twitter", v)} />
                <Field label="YouTube" value={data.social_media?.youtube || ""} onChange={(v) => update("social_media.youtube", v)} />
            </Section>

            <Section title="SEO Varsayılanları">
                <Field label="Meta Title" value={data.seo_defaults?.meta_title || ""} onChange={(v) => update("seo_defaults.meta_title", v)} />
                <Field label="Meta Description" value={data.seo_defaults?.meta_desc || ""} onChange={(v) => update("seo_defaults.meta_desc", v)} multiline />
            </Section>

            <Section title="Analytics & Entegrasyonlar">
                <Field label="Analytics ID" value={data.analytics_id} onChange={(v) => update("analytics_id", v)} />
                <Field label="SendGrid API Key" value={data.sendgrid_api_key} onChange={(v) => update("sendgrid_api_key", v)} />
                <Field label="Resend API Key" value={data.resend_api_key} onChange={(v) => update("resend_api_key", v)} testId="settings-resend-key" />
                <Field label="Gönderen E-posta" value={data.sender_email} onChange={(v) => update("sender_email", v)} />
                <Field label="Google Maps Embed URL" value={data.google_maps_embed_url} onChange={(v) => update("google_maps_embed_url", v)} multiline />
            </Section>

            <Section title="Bakım Modu">
                <div className="flex items-center justify-between bg-light-bg border border-slate-200 rounded-md px-4 py-3">
                    <div>
                        <div className="font-semibold text-dark-navy">Bakım Modu</div>
                        <div className="text-xs text-slate-500">Açıkken site ziyaretçilere bakım mesajı gösterir.</div>
                    </div>
                    <Switch
                        checked={!!data.maintenance_mode}
                        onCheckedChange={(v) => update("maintenance_mode", v)}
                        data-testid="settings-maintenance-toggle"
                    />
                </div>
                <Field
                    label="Bakım Mesajı"
                    value={data.maintenance_message || ""}
                    onChange={(v) => update("maintenance_message", v)}
                    multiline
                />
            </Section>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <section className="bg-white rounded-lg border border-slate-100 shadow-md p-5 space-y-4">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-deep-blue">{title}</h2>
            <div className="grid md:grid-cols-2 gap-4">{children}</div>
        </section>
    );
}

function Field({ label, value, onChange, multiline, testId }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-deep-blue/80 uppercase tracking-wider">{label}</span>
            {multiline ? (
                <textarea
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    className="mt-1 w-full rounded-md border border-slate-300 focus:border-teal px-3 py-2 text-sm outline-none"
                    data-testid={testId}
                />
            ) : (
                <input
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 focus:border-teal px-3 py-2 text-sm outline-none"
                    data-testid={testId}
                />
            )}
        </label>
    );
}
